import type { InSnipCard } from "./types";

/**
 * The feed lens: Top is the order the round was built in, For you floats followed topics
 * (then the categories this reader saves and reacts to) and Latest is article recency. The
 * lens RE-ORDERS the round, it never hides a story.
 *
 * ONE FUNCTION FOR THE ORDER AND THE LANDING CARD. Found live 2026-09-14: with Latest
 * remembered, a reload opened on card 10 of 10, the next on card 4. The deck was sorted by the
 * lens but the app still opened on the server's first card, which after the sort sat wherever
 * its date landed. Whatever is displayed first is where the round starts.
 */
export type Lens = "top" | "foryou" | "latest";

export type LensContext = {
  /** Categories the reader follows in "Make It Yours". */
  follows?: Set<string>;
  /** Saved card ids; each counts twice toward its category. */
  savedIds?: string[];
  /** Reactions by card id; each reacted card counts once toward its category. */
  reactions?: Record<string, string[]>;
  /** Resolves a saved or reacted id to its category when it is not in `pool`. */
  categoryOf?: (id: string) => string | undefined;
  /** Cards the reader has hidden; only landingCard() honours this, the order never hides. */
  hidden?: Set<string>;
};

export function lensOrder(pool: InSnipCard[], lens: Lens, ctx: LensContext): InSnipCard[] {
  if (lens === "latest") {
    return [...pool].sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""));
  }
  if (lens === "foryou") {
    const follows = ctx.follows ?? new Set<string>();
    const affinity = new Map<string, number>();
    const catOf = (id: string) => pool.find((x) => x.id === id)?.category ?? ctx.categoryOf?.(id);
    for (const id of ctx.savedIds ?? []) {
      const cat = catOf(id);
      if (cat) affinity.set(cat, (affinity.get(cat) ?? 0) + 2);
    }
    for (const [id, rs] of Object.entries(ctx.reactions ?? {})) {
      if (!Array.isArray(rs) || !rs.length) continue;
      const cat = catOf(id);
      if (cat) affinity.set(cat, (affinity.get(cat) ?? 0) + 1);
    }
    return pool
      .map((c, i) => ({
        c,
        i,
        s: (follows.has(c.category ?? "") ? 100 : 0) + (affinity.get(c.category ?? "") ?? 0),
      }))
      .sort((a, b) => b.s - a.s || a.i - b.i)
      .map((x) => x.c);
  }
  return pool;
}

/** The card a freshly loaded round opens on: the first one the reader will actually see. */
export function landingCard(pool: InSnipCard[], lens: Lens, ctx: LensContext): InSnipCard | null {
  const visible = ctx.hidden?.size ? pool.filter((c) => !ctx.hidden!.has(c.id)) : pool;
  return lensOrder(visible, lens, ctx)[0] ?? null;
}
